import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { PageShell } from "@/components/layout/PageShell";
import { PageHeader } from "@/components/ui/page-header";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import ArticleCard from "@/components/ui/ArticleCard";
import { ArticleCardSkeleton } from "@/components/ui/skeletons";
import { supabase } from "@/integrations/supabase/client";
import { toNewsArticle } from "@/lib/articleAdapter";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

export const PRESS_RELEASE_CATEGORY = "Press Release";
const PAGE_SIZE = 24;

const fetchPressReleases = async (page: number) => {
  const from = page * PAGE_SIZE;
  const { data, count, error } = await supabase
    .from("articles")
    .select(
      "id, title, excerpt, url, image_url, published_at, source_id, source_name, category, slug, author_name, likes_count, comments_count",
      { count: "exact" },
    )
    .eq("category", PRESS_RELEASE_CATEGORY)
    .eq("is_published", true)
    .order("published_at", { ascending: false })
    .range(from, from + PAGE_SIZE - 1);

  if (error) throw error;
  return { articles: (data ?? []).map(toNewsArticle), total: count ?? 0 };
};

const PressReleases = () => {
  const t = useT();
  const [page, setPage] = useState(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["press-releases", page],
    queryFn: () => fetchPressReleases(page),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const articles = data?.articles ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageShell>
      <Seo
        title={t("auto.press-releases-byteline-3e54")}
        description={t("auto.company-announcements-partnerships-and-f-e620")}
        path="/press-releases"
      />
      <PageHeader
        eyebrow={t("auto.press-releases-01d7")}
        title={t("auto.press-releases-01d7")}
        description={t("auto.announcements-submitted-by-companies-and-ae2d")}
      />

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <ArticleCardSkeleton key={i} />
          ))}
        </div>
      ) : isError ? (
        <div className="py-16 text-center">
          <h2 className="mb-2 text-xl font-semibold"><T k="auto.couldn-t-load-press-releases-2907" /></h2>
          <Button variant="outline" onClick={() => window.location.reload()}>
            <T k="auto.try-again-f915" />
          </Button>
        </div>
      ) : articles.length === 0 ? (
        <p className="py-16 text-center text-muted-foreground"><T k="auto.no-press-releases-yet-4851" /></p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map((article, index) => (
              <ArticleCard key={article.id} article={article} priority={index < 3} />
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="outline"
              disabled={page === 0}
              onClick={() => {
                setPage((p) => Math.max(0, p - 1));
                window.scrollTo({ top: 0 });
              }}
            >
              <T k="auto.previous-dd1f" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages} · {total.toLocaleString()} releases
            </span>
            <Button
              variant="outline"
              disabled={page + 1 >= totalPages}
              onClick={() => {
                setPage((p) => p + 1);
                window.scrollTo({ top: 0 });
              }}
            >
              <T k="auto.next-10ac" />
            </Button>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default PressReleases;
